import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIndexedFoldersStore } from '@/lib/stores/indexedFoldersStore'
import { FolderIndexStatusPanel } from './components/FolderIndexStatusPanel'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { type FolderIndexStatus } from '@/lib/api/indexedFoldersApi'

const FALLBACK_PENDING_STATUS: Omit<FolderIndexStatus, 'folderId'> = {
  documentCount: 0,
  folderCount: 0,
  status: 'pending',
}

export function IndexingStatusGrid() {
  const { t } = useTranslation()
  const [activeAction, setActiveAction] = useState<string | null>(null)
  const {
    folders,
    folderStatuses,
    indexingProgress,
    isLoading,
    error,
    pollAllFolderStatuses,
    stopPollingStatuses,
    fetchFolders,
    getFolderStatus,
    startIndexing,
  } = useIndexedFoldersStore()

  const activeFolders = useMemo(() => (folders ?? []).filter(folder => folder?.isActive), [folders])
  const statusesByFolder = useMemo(() => folderStatuses ?? {}, [folderStatuses])
  const indexedFolders = useMemo(
    () =>
      activeFolders.map(folder => ({
        folder,
        status: statusesByFolder[folder.id] ?? {
          folderId: folder.id,
          ...FALLBACK_PENDING_STATUS,
        },
      })),
    [activeFolders, statusesByFolder]
  )
  const totalDocuments = useMemo(
    () => indexedFolders.reduce((sum, folder) => sum + (folder.status.documentCount ?? 0), 0),
    [indexedFolders]
  )
  const hasAnyIndexing = useMemo(
    () => indexedFolders.some(folder => folder.status.status === 'indexing'),
    [indexedFolders]
  )
  const hasAnyError = useMemo(
    () => indexedFolders.some(folder => folder.status.status === 'error'),
    [indexedFolders]
  )

  const overallStatus = hasAnyError
    ? t('planvorming.indexStatus.error')
    : hasAnyIndexing
      ? t('planvorming.indexStatus.indexing')
      : indexedFolders.every(folder => folder.status.status === 'indexed') && indexedFolders.length > 0
        ? t('planvorming.ready')
        : t('planvorming.indexStatus.pending')

  const handleRefreshFolder = async (folderId: string) => {
    setActiveAction(`refresh:${folderId}`)
    try {
      await getFolderStatus(folderId)
    } finally {
      setActiveAction(null)
    }
  }

  const handleReindexFolder = async (folderId: string) => {
    setActiveAction(`reindex:${folderId}`)
    try {
      await startIndexing([folderId])
      await pollAllFolderStatuses()
    } finally {
      setActiveAction(null)
    }
  }

  useEffect(() => {
    if (activeFolders.length === 0) {
      void fetchFolders()
    }
  }, [activeFolders.length, fetchFolders])

  useEffect(() => {
    if (activeFolders.length > 0) {
      void pollAllFolderStatuses()
    }
  }, [activeFolders.length, pollAllFolderStatuses])

  useEffect(() => {
    if (hasAnyIndexing) {
      void pollAllFolderStatuses()
      return
    }

    stopPollingStatuses()

    return () => {
      stopPollingStatuses()
    }
  }, [hasAnyIndexing, pollAllFolderStatuses, stopPollingStatuses])

  if (isLoading && activeFolders.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index} className="p-4 border-dashed">
            <div className="space-y-4 animate-pulse">
              <div className="h-5 w-3/4 bg-muted rounded" />
              <div className="h-4 w-1/2 bg-muted rounded" />
              <div className="h-4 w-2/3 bg-muted rounded" />
              <div className="h-2 w-full bg-muted rounded" />
            </div>
          </Card>
        ))}
      </div>
    )
  }

  if (!isLoading && activeFolders.length === 0) {
    return (
      <div className="bg-muted/50 rounded-lg p-8 text-center">
        <p className="text-muted-foreground">{t('planvorming.noIndexedFolders')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground">{t('planvorming.totalDocuments')}</div>
            <div className="text-2xl font-bold">{totalDocuments}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground">{t('planvorming.activeFolders')}</div>
            <div className="text-2xl font-bold">{activeFolders.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm font-medium text-muted-foreground">{t('planvorming.indexingStatus')}</div>
            <div className="text-2xl font-bold">{overallStatus}</div>
          </CardContent>
        </Card>
      </div>

      {/* Grid of folder status panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {indexedFolders.map(({ folder, status }) => (
          <FolderIndexStatusPanel
            key={folder.id}
            status={status}
            folderName={folder.name}
            progressPercent={indexingProgress?.[folder.id]}
            onRefresh={() => void handleRefreshFolder(folder.id)}
            onReindex={() => void handleReindexFolder(folder.id)}
            isRefreshing={activeAction === `refresh:${folder.id}`}
            isReindexing={activeAction === `reindex:${folder.id}`}
          />
        ))}
      </div>
    </div>
  )
}
